// src/pages/DBCBuyPage.tsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useInitializePaymentMutation } from "../../../../app/api/dBCApi";
import { Menu, X } from "lucide-react";
import PlyrPlayer from "../../../../components/common/PlyrPlayer";
import { WhatIsThisSection } from "./WhatIsThisSection";
import { WhyJoinSection } from "./WhyJoinSection";
import { CaseStudiesSection } from "./CaseStudiesSection";
import { CurriculumSection } from "./CurriculumSection";
// import { TestimonialsSection } from "./TestimonialsSection";
import { FAQsSection } from "./FAQsSection";
import { BonusesSection } from "./BonusesSection";
import { EnrollmentSection } from "./EnrollmentSection";
import PaymentOptions from "../../../../components/common/PaymentOptions";
import { InstructorSection } from "./InstructorSection";
import modulesData from "../../../../data/modules.json";
import bonusModules from "../../../../data/dibBonusModules.json";
import adeoluImage from "../../../../assets/images/Blurred suit.png";
// ─── theme ────────────────────────────────────────────────────────
const THEME = {
  primaryColor: "#1d4ed8",
};

// ─── countdown ────────────────────────────────────────────────────
const CountdownTimer: React.FC<{
  endDate?: string | Date;
  storageKey?: string;
}> = ({ endDate, storageKey = "dbc-buy-countdown" }) => {
  const [timeLeft, setTimeLeft] = React.useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [finalEndDate, setFinalEndDate] = React.useState<Date | null>(null);

  React.useEffect(() => {
    const stored = localStorage.getItem(storageKey);
    const storedEnd = stored ? new Date(stored) : null;
    const parsed = typeof endDate === "string" ? new Date(endDate) : endDate;
    const resolved =
      storedEnd ?? parsed ?? new Date(Date.now() + 3 * 24 * 60 * 60 * 1000);
    if (!stored) localStorage.setItem(storageKey, resolved.toISOString());
    setFinalEndDate(resolved);
  }, [endDate, storageKey]);

  React.useEffect(() => {
    if (!finalEndDate) return;
    const calc = () => {
      const diff = finalEndDate.getTime() - Date.now();
      if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      return {
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff / 3600000) % 24),
        minutes: Math.floor((diff / 60000) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      };
    };
    setTimeLeft(calc());
    const t = setInterval(() => setTimeLeft(calc()), 1000);
    return () => clearInterval(t);
  }, [finalEndDate]);

  const fmt = (n: number) => String(n).padStart(2, "0");

  return (
    <div className="flex items-center justify-center gap-3 sm:gap-5">
      {[
        { value: timeLeft.days, label: "Days" },
        { value: timeLeft.hours, label: "Hours" },
        { value: timeLeft.minutes, label: "Minutes" },
        { value: timeLeft.seconds, label: "Seconds" },
      ].map((item, i) => (
        <div
          key={i}
          className="flex flex-col items-center justify-center bg-white/20 backdrop-blur-sm rounded-md w-14 sm:w-16 h-14 sm:h-16 shadow-inner"
        >
          <span className="text-white text-lg sm:text-xl font-extrabold leading-none">
            {fmt(item.value)}
          </span>
          <span className="text-white/80 text-[10px] sm:text-xs uppercase tracking-wide mt-1">
            {item.label}
          </span>
        </div>
      ))}
    </div>
  );
};

// ─── hardcoded salesPage data ──────────────────────────────────────
const SALES_PAGE_DATA = {
  title: "Digital Income Blueprint",
  design: { theme: THEME },

  // ── navigation ──
  navigation: {
    enabled: true,
    sticky: true,
    tabs: [
      { label: "Overview", targetSection: "welcome", visible: true, order: 1 },
      {
        label: "Story",
        targetSection: "case-studies",
        visible: true,
        order: 2,
      },
      {
        label: "Curriculum",
        targetSection: "curriculum",
        visible: true,
        order: 3,
      },
      {
        label: "Testimonials",
        targetSection: "testimonials",
        visible: true,
        order: 4,
      },
      { label: "Bonuses", targetSection: "bonuses", visible: true, order: 5 },
      { label: "Enroll", targetSection: "enrollment", visible: true, order: 6 },
      { label: "FAQs", targetSection: "faqs", visible: true, order: 7 },
    ],
    cta: { text: "ENROLL NOW", url: "#enrollment" },
  },

  // ── countdown ──
  countdown: {
    enabled: true,
    text: "🎓 ENROLLMENT OPEN — Offer ends in:",
  },

  // ── welcome / hero ──
  courseContent: {
    welcome: {
      preHeader: "Proven 4-Week System for Nigerian Professionals",
      header:
        "Build a ₦500K–1M/Month Online Business Without Quitting Your Job",
      postHeader:
        "The battle-tested system that helps time-starved Nigerian professionals turn their expertise into profitable online income — even if you only have 1 hour daily.",
      heroText:
        "You're not lazy. You're not lacking ambition. You're trapped in a system that wasn't designed for your financial freedom. That ends today.",
      videoUrl: "", // set to your YouTube embed ID if available
    },

    // ── what is this (pain points) ──
    whatIsThis: {
      sectionTitle: "If This Is You, Keep Reading…",
      description:
        "You work hard, you build competence, and you get paid a salary — usually tied to a job that could disappear overnight. The core difference between you and people earning online isn't talent — it's strategy.",
      features: [
        {
          order: 1,
          featureHeading: "Single income risk",
          featureText:
            "One payroll, one company, one source — if it disappears, everything stops.",
        },
        {
          order: 2,
          featureHeading: "Time-for-money trap",
          featureText:
            "Your income grows only if you trade more hours or climb the corporate ladder — both have a hard ceiling.",
        },
        {
          order: 3,
          featureHeading: "Market confusion",
          featureText:
            "You don't know which skill will actually sell, to whom, or how to package it without sounding desperate.",
        },
        {
          order: 4,
          featureHeading: "Tried and failed before",
          featureText:
            "You've tried dropshipping, affiliate marketing, or random side hustles — got overwhelmed by tech, made zero sales, and gave up.",
        },
      ],
    },

    // ── why join (the solution) ──
    whyJoin: {
      sectionTitle: "Three Things That Change Everything",
      description:
        "Once you apply the D.B.C. Formula™, you remove guesswork and create income that scales without more of your time.",
      benefits: [
        {
          order: 1,
          benefitHeading: "Discover your profitable skill",
          benefitText:
            "Stop guessing. Find which of your existing skills will actually sell and to which Nigerian buyer — using a proven validation checklist before you build anything.",
        },
        {
          order: 2,
          benefitHeading: "Build authority that earns trust",
          benefitText:
            "Grow an audience that trusts you on email and WhatsApp. When your list trusts you, selling feels natural — not pushy.",
        },
        {
          order: 3,
          benefitHeading: "Convert with automated funnels",
          benefitText:
            "Deploy small, automated sales paths that sell while you sleep — no complicated tech, no cold DMs, no begging for sales.",
        },
      ],
    },

    // ── case studies (success stories) ──
    caseStudies: {
      sectionTitle: "They Were Exactly Where You Are Now",
      description:
        "These aren't celebrities or tech geniuses. They're everyday Nigerian professionals who decided 'enough is enough.'",
      studies: [
        {
          order: 1,
          caseStudyHeading: "Teacher → Exam Coach",
          caseStudyDescription:
            "Secondary school teacher earning ₦85K monthly. DBC can help you package exam prep coaching for students.",
          caseStudyResults: [
            {
              value: "₦109K",
              metric: "Potential in First Month",
              description: "Month 1 income",
            },
            {
              value: "₦497K",
              metric: "Month 3",
              description: "Three months in",
            },
          ],
          testimonial:
            "You don't have to quit teaching yet, but DBC gives you OPTIONS. It gives you clarity on what to sell and exactly who to sell it to.",
          studentName: "",
          studentTitle: "",
        },
        {
          order: 2,
          caseStudyHeading: "HR Officer → HR Consultant",
          caseStudyDescription:
            "Works in HR, 8–5 daily. Zero tech skills. DBC can show you how to sell HR consulting to small businesses.",
          caseStudyResults: [
            {
              value: "₦50K",
              metric: "Week 3",
              description: "First sale in week 3",
            },
            { value: "₦350K", metric: "Monthly", description: "Weekends only" },
          ],
          testimonial:
            "Your boss doesn't even have to know because your business will not affect your job. DBC helps you batch your work on weekends and the funnels handle the rest.",
          studentName: "",
          studentTitle: "",
        },
        {
          order: 3,
          caseStudyHeading: "Accountant → Tax Consultant",
          caseStudyDescription:
            "Maybe you tried dropshipping and affiliate marketing — and nothing worked. DBC can help you package your accounting knowledge.",
          caseStudyResults: [
            {
              value: "₦180K",
              metric: "First Month",
              description: "Month 1 income",
            },
            {
              value: "₦520K",
              metric: "Month 4",
              description: "You can resign from 9-5",
            },
          ],
          testimonial: "The system works — you just have to follow it.",
          studentName: "",
          studentTitle: "",
        },
      ],
    },

    // ── curriculum (built from revisedModules.json structure) ──
    curriculum: {
      sectionTitle: "The D.B.C. Formula™ — Full Curriculum",
      description:
        "A battle-tested, 3-step system: Discover → Build → Convert. Takes you from 'I have an idea' to 'I just made my first ₦50K' in 4 weeks.",
      totalDuration: "4 weeks core + 3 months support",
      modules: [
        ...modulesData.modules.map((mod: any, i: number) => ({
          order: i + 1,
          title: mod.title,
          description: mod.subtitle,
          duration: mod.duration,
          lessons: (mod.sessions || []).map((session: any, j: number) => ({
            order: j + 1,
            lessonTitle: session.title,
            lessonDescription: (session.topics || []).join(" · "),
            lessonType: "video",
            isPreview: j === 0 && i === 0,
          })),
        })),
        {
          order: modulesData.modules.length + 1,
          title: "Weeks 5–12 — Coaching & Scale",
          description:
            "Weekly live coaching calls, personalised feedback on your funnel submissions, and systematic optimization to double what's already working.",
          duration: "8 weeks",
          lessons: [
            {
              order: 1,
              lessonTitle: "Bi-weekly live coaching calls with Q&A",
              lessonType: "live",
            },
            {
              order: 2,
              lessonTitle: "Funnel review & personalised feedback",
              lessonType: "live",
            },
            {
              order: 3,
              lessonTitle: "Private WhatsApp/Telegram community",
              lessonType: "community",
            },
            {
              order: 4,
              lessonTitle: "Scale to ₦200K–500K/month",
              lessonType: "video",
            },
          ],
        },
      ],
    },

    // ── testimonials ──
    // testimonials: [
    //   {
    //     order: 1,
    //     featured: true,
    //     rating: 5,
    //     testimonialText:
    //       "Common thread among everyone who succeeds: they all had 'normal' jobs, felt stuck, then took action. Now they control their income — not their employer. The D.B.C. Formula is the reason.",
    //     studentName: "DBC Cohort",
    //     studentTitle: "Multiple graduates",
    //   },
    //   {
    //     order: 2,
    //     featured: false,
    //     rating: 5,
    //     testimonialText:
    //       "Within 3 weeks after Module 1 I had my first 20 paying customers. I made ₦180,000 in my first month while still working my 9–5.",
    //     studentName: "Chiamaka O.",
    //     studentTitle: "HR Manager, Lagos",
    //   },
    //   {
    //     order: 3,
    //     featured: false,
    //     rating: 5,
    //     testimonialText:
    //       "SweetSpot Formula gave me clarity. Within 3 months I had an automated funnel earning ₦350,000/month.",
    //     studentName: "Emeka I.",
    //     studentTitle: "IT Consultant, Abuja",
    //   },
    //   {
    //     order: 4,
    //     featured: false,
    //     rating: 5,
    //     testimonialText:
    //       "I batch content for 2 hours each weekend. WhatsApp list grew to 400 in 6 weeks — ₦420k last month.",
    //     studentName: "Fatima M.",
    //     studentTitle: "Banking Professional, Port Harcourt",
    //   },
    // ],

    // ── faqs ──
    faqs: {
      sectionTitle: "Frequently Asked Questions",
      description: "Everything you need to know before enrolling.",
      questions: [
        {
          order: 1,
          faqQuestion: "How much time do I need each week?",
          faqAnswer:
            "Just 2 hours daily during the 4-week core phase. Coaching weeks require even less — mostly attending live calls and reviewing feedback.",
        },
        {
          order: 2,
          faqQuestion: "Do I need any tech skills?",
          faqAnswer:
            "None. The system is built for complete beginners. We walk you through every tool step by step — if you can use WhatsApp, you can follow this program.",
        },
        {
          order: 3,
          faqQuestion: "What if I've tried online business before and failed?",
          faqAnswer:
            "Most people fail because they pick the wrong thing to sell or try to build everything before validating. DBC fixes that in Week 1 — you validate before you build anything.",
        },
        {
          order: 4,
          faqQuestion: "When will I make my first sale?",
          faqAnswer:
            "Our students typically make their first sale within 3 weeks. Some make it in Week 1. The guarantee: implement the system and make at least ₦50K in 30 days or get a full refund.",
        },
        {
          order: 5,
          faqQuestion: "Can I pay in installments?",
          faqAnswer:
            "Yes — you can split the payment into 2 installments. Both options give you full access, all support, and the same 30-day guarantee.",
        },
        {
          order: 6,
          faqQuestion: "What is the money-back guarantee?",
          faqAnswer:
            "Follow the system, implement the strategies, and if you don't make at least ₦50,000 in your first 30 days OR feel this wasn't worth every kobo, email us for a full refund. No hard feelings.",
        },
      ],
    },

    // ── bonuses ──
    bonuses: {
      sectionTitle: "3 Game-Changing Bonus Modules — Worth ₦75,000, Yours FREE",
      description:
        "These alone could be sold as separate courses. You get them included when you enroll today.",
      items: bonusModules.bonusModules.map((bonus: any, i: number) => ({
        order: i + 1,
        bonusTitle: bonus.title,
        bonusDescription: bonus.description,
        bonusValue: `BONUS #${i + 1}`,
      })),
    },

    // ── enrollment / pricing ──
    enrollment: {
      sectionTitle: "Invest In Your Financial Freedom Today",
      description:
        "Less than the price of a weekend outing — yet this could be the decision that changes your financial trajectory forever. Both plans include everything.",
      fullPayment: {
        enabled: true,
        isPopular: true,
        label: "Full Payment",
        displayPrice: "₦14,973",
        savings: "₦10,000 vs split",
        features: [
          "4-week intensive practical training",
          "3 months ongoing support",
          "Bi-weekly live coaching calls",
          "50+ worksheets, templates & checklists",
          "Private WhatsApp/Telegram community",
          "3 bonus modules (₦75K value)",
          "Lifetime access + future updates",
          "30-day money-back guarantee",
        ],
        buttonText: "Enroll Now — Full Payment",
        buttonUrl: "#enroll-full",
      },
      paymentPlan: {
        enabled: true,
        isPopular: false,
        label: "Split Payment",
        displayPrice: "₦9,999",
        numberOfPayments: 2,
        totalPrice: "Total: ₦19,998",
        features: [
          "4-week intensive practical training",
          "3 months ongoing support",
          "Bi-weekly live coaching calls",
          "50+ worksheets, templates & checklists",
          "Private WhatsApp/Telegram community",
          "3 bonus modules (₦75K value)",
          "Lifetime access + future updates",
          "30-day money-back guarantee",
        ],
        buttonText: "Start with ₦9,999",
        buttonUrl: "#enroll-split",
      },
      guarantee: {
        enabled: true,
        title: "30-Day 'Show Me The Money' Guarantee",
        description:
          "Follow the system. Implement the strategies. If you don't make at least ₦50,000 in your first 30 days OR feel this wasn't worth every kobo, email us and we'll refund you in full. No hard feelings. We stay friends. You risk NOTHING — we risk everything.",
      },
    },

    // ── instructor ──
    instructor: {
      sectionTitle: "Meet Your Instructor",
      name: "Adeolu Adegbemi",
      imageUrl: adeoluImage,
      title: "Founder, Digital Income Blueprint",
      bio: "A busy Nigerian professional who turned one regular skill into a repeatable online product while keeping a full-time job. Within months, side income matched a significant portion of salary — working just 2–3 hours a week. The D.B.C. Formula is the exact system used, refined across hundreds of students.",
      credentials: [
        "Built profitable online funnels while employed full-time",
        "Helped several professionals monetize their skills",
        "Specialist in Nigeria-specific digital product sales and WhatsApp funnels",
      ],
      socialLinks: {},
    },

    // ── footer cta ──
    cta: {
      ctaHeading: "Two Paths. One Decision. Today.",
      ctaText:
        "The difference between staying stuck and earning ₦200K–500K/month? One click. One decision. One moment of courage. Class starts soon — spots are limited.",
      primaryButtonText: "Start Your Journey Now →",
      primaryButtonUrl: "#enrollment",
    },
  },

  settings: { showPoweredBy: false },
};

// ─── page ─────────────────────────────────────────────────────────
const DBCBuyPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState("welcome");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<"full" | "split">(
    "full",
  );

  const [initializePayment] = useInitializePaymentMutation();

  const { courseContent, navigation, countdown, design, settings } =
    SALES_PAGE_DATA;
  const theme = design.theme;

  const fullPrice = 1497300; // kobo
  const splitFirst = 999900;
  const splitSecond = 999900;

  const features = courseContent.enrollment.fullPayment.features;

  const handleEnrollmentSubmit = async (payload: {
    name: string;
    email: string;
    phone: string;
    paymentPlan: "full" | "split";
  }) => {
    try {
      const amount =
        (payload.paymentPlan === "full" ? fullPrice : splitFirst) / 100;
      const res = await initializePayment({
        email: payload.email,
        name: payload.name,
        amount,
        paymentPlan: payload.paymentPlan,
        metadata: { phone: payload.phone, course: "Digital Income Blueprint" },
      }).unwrap();
      if (res?.authorization_url) {
        window.location.href = res.authorization_url;
      } else {
        alert("Payment could not be initialized. Please try again.");
      }
    } catch (err: any) {
      console.error("Payment init error:", err);
      alert(err?.data?.message || err?.message || "Something went wrong.");
    }
  };

  const scrollToSection = (sectionId: string) => {
    const el = document.getElementById(sectionId);
    if (el) {
      const offset = 80;
      window.scrollTo({
        top: el.getBoundingClientRect().top + window.pageYOffset - offset,
        behavior: "smooth",
      });
      setActiveTab(sectionId);
      setMobileNavOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* ── Countdown banner ── */}
      {countdown?.enabled && (
        <div className="py-2" style={{ backgroundColor: theme.primaryColor }}>
          <div className="max-w-7xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4">
            <span className="text-white text-sm sm:text-base font-medium">
              {countdown.text}
            </span>
            <CountdownTimer />
          </div>
        </div>
      )}

      {/* ── Navigation ── */}
      {navigation?.enabled && (
        <nav
          className={`px-2 bg-white shadow-md ${navigation.sticky ? "sticky top-0 z-40" : ""}`}
        >
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between h-16">
              <h1 className="text-sm sm:text-base font-semibold text-gray-900 uppercase truncate max-w-[160px]">
                {SALES_PAGE_DATA.title}
              </h1>

              {/* desktop tabs */}
              <div className="hidden lg:flex items-center gap-1">
                {[...navigation.tabs]
                  .filter((t) => t.visible)
                  .sort((a, b) => a.order - b.order)
                  .map((tab) => (
                    <button
                      key={tab.targetSection}
                      onClick={() => scrollToSection(tab.targetSection)}
                      className={`text-xs font-medium px-2 py-1 border-b-2 transition-colors ${
                        activeTab === tab.targetSection
                          ? ""
                          : "text-gray-600 hover:text-gray-900 border-transparent"
                      }`}
                      style={{
                        borderBottomColor:
                          activeTab === tab.targetSection
                            ? theme.primaryColor
                            : "transparent",
                        color:
                          activeTab === tab.targetSection
                            ? theme.primaryColor
                            : undefined,
                      }}
                    >
                      {tab.label}
                    </button>
                  ))}
              </div>

              <div className="hidden lg:block">
                <button
                  onClick={() => scrollToSection("enrollment")}
                  className="px-4 py-2 text-white text-sm font-bold rounded-md hover:opacity-90 transition uppercase"
                  style={{ backgroundColor: theme.primaryColor }}
                >
                  ENROLL NOW
                </button>
              </div>

              {/* mobile toggle */}
              <button
                className="lg:hidden p-2 rounded-md text-gray-700 hover:bg-gray-100"
                onClick={() => setMobileNavOpen((v) => !v)}
              >
                {mobileNavOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>

            {/* mobile menu */}
            {mobileNavOpen && (
              <div className="lg:hidden pb-4 space-y-2 mt-2">
                {[...navigation.tabs]
                  .filter((t) => t.visible)
                  .sort((a, b) => a.order - b.order)
                  .map((tab) => (
                    <button
                      key={tab.targetSection}
                      onClick={() => scrollToSection(tab.targetSection)}
                      className="w-full text-left px-3 py-2 rounded text-sm font-medium transition-colors"
                      style={{
                        backgroundColor:
                          activeTab === tab.targetSection
                            ? theme.primaryColor
                            : "#f3f4f6",
                        color:
                          activeTab === tab.targetSection ? "#fff" : "#374151",
                      }}
                    >
                      {tab.label}
                    </button>
                  ))}
                <button
                  onClick={() => scrollToSection("enrollment")}
                  className="w-full mt-2 py-2 text-white text-sm font-bold rounded-md"
                  style={{ backgroundColor: theme.primaryColor }}
                >
                  ENROLL NOW
                </button>
              </div>
            )}
          </div>
        </nav>
      )}

      {/* ── Hero ── */}
      <section
        id="welcome"
        className="relative bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 text-white overflow-hidden"
      >
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.05) 35px, rgba(255,255,255,.05) 70px)`,
            }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
          {/* enrollment badge */}
          <div className="text-center mb-6">
            <span className="inline-block bg-red-600 px-6 py-2 rounded-full text-sm font-bold shadow-lg">
              🎓 ENROLLMENT NOW OPEN — LIMITED SPOTS
            </span>
          </div>

          {courseContent.welcome.preHeader && (
            <p className="text-center text-base sm:text-lg text-gray-300 mb-3">
              {courseContent.welcome.preHeader}
            </p>
          )}

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-center leading-tight mb-6">
            {courseContent.welcome.header}
          </h1>

          {courseContent.welcome.postHeader && (
            <p className="text-lg sm:text-xl lg:text-2xl text-center text-gray-300 max-w-4xl mx-auto mb-8">
              {courseContent.welcome.postHeader}
            </p>
          )}

          {/* quick stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mb-10">
            {[
              { value: "4 Weeks", label: "To Launch & Earn" },
              { value: "₦396K+", label: "Avg. Monthly Income" },
              { value: "1 Hour", label: "Daily Time Needed" },
              { value: "97%", label: "Success Rate" },
            ].map(({ value, label }) => (
              <div
                key={label}
                className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-4 py-4 text-center"
              >
                <div className="text-2xl font-bold text-yellow-400">
                  {value}
                </div>
                <div className="text-xs text-white/70 mt-1">{label}</div>
              </div>
            ))}
          </div>

          {/* CTA buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <button
              onClick={() => scrollToSection("enrollment")}
              className="inline-block bg-red-600 text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-red-700 transition-all transform hover:scale-105 shadow-xl"
            >
              Enroll Now — Class Starts Soon
            </button>
            <button
              onClick={() => scrollToSection("curriculum")}
              className="inline-block bg-white/10 backdrop-blur-sm text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-white/20 transition-all border-2 border-white/30"
            >
              See Full Curriculum
            </button>
          </div>

          {/* trust chips */}
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            {[
              "First sale within 3 weeks",
              "No tech skills needed",
              "Keep your 9-5 job",
            ].map((t) => (
              <div
                key={t}
                className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full"
              >
                <span className="text-green-400">✓</span>
                <span>{t}</span>
              </div>
            ))}
          </div>

          {/* video */}
          {courseContent.welcome.videoUrl && (
            <div className="max-w-2xl mx-auto mt-10">
              <div className="aspect-video rounded-lg overflow-hidden shadow-2xl border-4 border-white/20">
                <PlyrPlayer
                  provider="youtube"
                  embedId={courseContent.welcome.videoUrl}
                  options={{
                    controls: [
                      "play-large",
                      "play",
                      "progress",
                      "current-time",
                      "mute",
                      "volume",
                      "fullscreen",
                    ],
                    youtube: { noCookie: true, rel: 0 },
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* curved bottom */}
        <div className="relative">
          <svg
            viewBox="0 0 1440 80"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full h-auto"
          >
            <path
              d="M0 0L60 8C120 16 240 32 360 40C480 48 600 48 720 40C840 32 960 16 1080 16C1200 16 1320 32 1380 40L1440 48V80H0V0Z"
              fill="white"
            />
          </svg>
        </div>
      </section>

      {/* ── Hero text ── */}
      {courseContent.welcome.heroText && (
        <div className="max-w-4xl mx-auto text-center py-10 px-4">
          <p className="text-xl sm:text-2xl lg:text-3xl font-semibold leading-relaxed text-gray-700">
            {courseContent.welcome.heroText}
          </p>
        </div>
      )}

      {/* ── Shared section components (same as CourseMasterclassView) ── */}
      <WhatIsThisSection data={courseContent.whatIsThis} theme={theme} />
      <WhyJoinSection data={courseContent.whyJoin} theme={theme} />
      <CaseStudiesSection data={courseContent.caseStudies} theme={theme} />
      <CurriculumSection data={courseContent.curriculum} theme={theme} />
      {/* <TestimonialsSection data={courseContent.testimonials} theme={theme} /> */}
      <FAQsSection data={courseContent.faqs} theme={theme} />
      <BonusesSection data={courseContent.bonuses} theme={theme} />

      {/* ── Value stack (pre-enrollment) ── */}
      <section className="py-16 px-6 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-3xl mx-auto bg-white rounded-2xl p-8 md:p-12 shadow-2xl border-2 border-blue-200">
          <h3 className="text-2xl font-bold text-center mb-8 text-gray-900">
            Total Value Breakdown
          </h3>
          <div className="space-y-4 text-lg">
            {[
              { label: "4-Week Core Program", value: "₦85,000" },
              { label: "3 Bonus Modules", value: "₦75,000" },
              { label: "3 Months Ongoing Support", value: "₦120,000" },
              { label: "Templates & Worksheets (50+)", value: "₦45,000" },
              { label: "Lifetime Access & Updates", value: "₦50,000" },
            ].map(({ label, value }, i, arr) => (
              <div
                key={label}
                className={`flex justify-between items-center pb-3 ${i < arr.length - 1 ? "border-b border-gray-200" : "border-b-2 border-gray-400"}`}
              >
                <span className="text-gray-700">{label}</span>
                <span className="font-bold text-gray-900">{value}</span>
              </div>
            ))}
            <div className="flex justify-between items-center pt-4">
              <span className="text-xl font-bold text-gray-900">
                Total Value:
              </span>
              <span
                className="text-3xl font-extrabold"
                style={{ color: theme.primaryColor }}
              >
                ₦375,000
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Enrollment section (uses EnrollmentSection component) ── */}
      <EnrollmentSection data={courseContent.enrollment} theme={theme} />

      {/* ── PaymentOptions widget (DBC-specific Paystack flow) ── */}
      <section id="enroll-form" className="py-8 px-6">
        <div className="max-w-4xl mx-auto">
          <PaymentOptions
            fullPrice={fullPrice}
            splitFirst={splitFirst}
            splitSecond={splitSecond}
            features={features}
            initialSelected={selectedPayment}
            onSelect={(plan: any) => setSelectedPayment(plan)}
            onSubmit={handleEnrollmentSubmit}
            labels={{
              full: "Full Payment (Save ₦10,000)",
              split: "Split Payment (Start with Less)",
            }}
          />
        </div>
      </section>

      <InstructorSection data={courseContent.instructor} theme={theme} />

      {/* ── Footer CTA ── */}
      {courseContent.cta && (
        <section
          className="py-16 text-white"
          style={{ backgroundColor: theme.primaryColor }}
        >
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              {courseContent.cta.ctaHeading}
            </h2>
            <p className="text-lg sm:text-xl mb-8 opacity-90 max-w-2xl mx-auto">
              {courseContent.cta.ctaText}
            </p>
            <button
              onClick={() => scrollToSection("enrollment")}
              className="inline-block px-10 py-4 bg-white font-bold rounded-lg hover:bg-gray-100 transition-colors text-lg shadow-xl"
              style={{ color: theme.primaryColor }}
            >
              {courseContent.cta.primaryButtonText}
            </button>
          </div>
        </section>
      )}

      {/* ── Trust badges ── */}
      <section className="py-12 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid sm:grid-cols-3 gap-8 text-center">
            {[
              {
                icon: "🛡️",
                title: "30-Day Money-Back",
                sub: "100% risk-free enrollment",
              },
              {
                icon: "♾️",
                title: "Lifetime Access",
                sub: "Keep learning forever",
              },
              {
                icon: "👥",
                title: "3 Months Support",
                sub: "We're with you every step",
              },
            ].map(({ icon, title, sub }) => (
              <div key={title}>
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3 text-3xl bg-gray-100">
                  {icon}
                </div>
                <div className="font-bold text-lg text-gray-900 mb-1">
                  {title}
                </div>
                <div className="text-sm text-gray-500">{sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-gray-900 text-white py-10">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h3 className="text-xl font-bold mb-1">Digital Income Blueprint</h3>
          <p className="text-gray-400 text-sm mb-6">
            Turn your expertise into income. Keep your job. Build your freedom.
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-400 mb-4">
            <Link to="/privacy" className="hover:text-white transition">
              Privacy Policy
            </Link>
            <Link to="/terms" className="hover:text-white transition">
              Terms of Service
            </Link>
            <Link to="/contact" className="hover:text-white transition">
              Contact Us
            </Link>
            <Link to="/faq" className="hover:text-white transition">
              FAQs
            </Link>
          </div>
          <p className="text-xs text-gray-600">
            © {new Date().getFullYear()} D.B.C. Formula. All rights reserved.
          </p>
        </div>
      </footer>

      {settings?.showPoweredBy && (
        <div className="py-4 bg-gray-50 border-t text-center text-sm text-gray-500">
          Powered by{" "}
          <span className="font-semibold text-gray-700">Your Platform</span>
        </div>
      )}
    </div>
  );
};

export default DBCBuyPage;
